import { useAssessmentMetrics } from '@app/src/hooks/useAssessmentMetrics'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { Box, Typography } from '@mui/material'
import React from 'react'
import type Assessment from '../types/assessment'

interface ContainerDimensions {
  width: string
  height: string
}

interface SegmentDimensions {
  width?: number // omitted => the segment flexes to fill the available row width
  height: number
  marginX: number
}

interface RatingBarProps {
  level?: number
  levelsCount?: number
  assessmentType?: AssessmentType
  percentage?: number
  hideLabel?: boolean
  preview?: boolean
  containerDimensions?: ContainerDimensions
  segmentDimensions?: SegmentDimensions
}

const DEFAULT_CONTAINER_DIMENSIONS: ContainerDimensions = { width: '52px', height: '10px' }
const DEFAULT_SEGMENT_DIMENSIONS: SegmentDimensions = { width: 8, height: 6, marginX: 1 }

const THREE_LEVEL_CONTAINER_DIMENSIONS: ContainerDimensions = { width: '80px', height: '24px' }
const THREE_LEVEL_SEGMENT_DIMENSIONS: SegmentDimensions = { width: 14.6667, height: 6, marginX: 1 }

const RatingBar: React.FC<RatingBarProps> = ({
  level = 1,
  levelsCount = 5,
  assessmentType,
  percentage,
  hideLabel = false,
  preview = false,
  containerDimensions,
  segmentDimensions,
}) => {
  const mockAssessment = {
    totalLevel: level,
    percentage,
    assessmentTemplate: {
      assessmentType: assessmentType || AssessmentType.BaselineAssessment,
      levels: levelsCount,
    },
  } as Assessment

  const { getCircleColor } = useAssessmentMetrics(mockAssessment)
  const activeColor = getCircleColor()
  const inactiveColor = '#E0E0E0'

  const isThreeLevel = levelsCount === 3
  const container =
    containerDimensions ?? (isThreeLevel ? THREE_LEVEL_CONTAINER_DIMENSIONS : DEFAULT_CONTAINER_DIMENSIONS)
  const segment = segmentDimensions ?? (isThreeLevel ? THREE_LEVEL_SEGMENT_DIMENSIONS : DEFAULT_SEGMENT_DIMENSIONS)
  const fillsAvailableWidth = container.width === '100%'

  const radius = 3
  const safeLevel = Math.max(1, Math.min(level, levelsCount))

  const containerStyles = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: preview ? 'transparent' : 'white',
    border: preview ? 'none' : '1px solid white',
    borderRadius: '4px',
  }

  const segments = Array.from({ length: levelsCount }, (_, i) => {
    const fillColor = i < safeLevel ? activeColor : inactiveColor
    const isFirst = i === 0
    const isLast = i === levelsCount - 1

    return (
      <Box
        key={i}
        height={`${segment.height}px`}
        bgcolor={fillColor}
        marginX={`${segment.marginX}px`}
        sx={{
          ...(segment.width === undefined ? { flex: 1, minWidth: 0 } : { width: `${segment.width}px` }),
          borderTopLeftRadius: isFirst ? `${radius}px` : 0,
          borderBottomLeftRadius: isFirst ? `${radius}px` : 0,
          borderTopRightRadius: isLast ? `${radius}px` : 0,
          borderBottomRightRadius: isLast ? `${radius}px` : 0,
        }}
      />
    )
  })

  return (
    <Box display="flex" alignItems="center" width={fillsAvailableWidth ? '100%' : undefined}>
      <Box
        display="flex"
        alignItems="center"
        sx={{
          ...containerStyles,
          width: container.width,
          height: container.height,
        }}
      >
        {segments}
      </Box>
      {!hideLabel && (
        <Box display="flex" alignItems="center" ml="8px">
          <Typography variant="body1" color="text.primary">
            {safeLevel}
          </Typography>
          <Typography variant="body1" color="text.disabled">
            /{levelsCount}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default RatingBar
